import { Box, Button, Flex, Stack, Text, Wrap } from "@chakra-ui/react";
import { ApiErrorProvider } from "@raiden/library/components/ReactHookForm/ApiError";
import { useConfiguration } from "@raiden/library/hooks/useConfiguration";
import generateFrontPath from "@raiden/library/libraries/utils/generateFrontPath";
import { useCallback, useState } from "react";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { FormattedMessage, useIntl } from "react-intl";
import { CenteredContent } from "../../components/CenteredContent";
import { Href } from "../../components/Href";
import { PageLayout } from "../../components/PageLayout";
import { Splash } from "../../components/Splash";
import { ContactsCreateForm } from "../Contacts/Create/Form";
import ContactsCreateHandler from "../Contacts/Create/Handler";
import { PersonnalInformation } from "./Information";

export function ContactUs() {
  const intl = useIntl();

  const [isSuccess, setIsSuccess] = useState(false);

  const { configuration } = useConfiguration();

  const environment = configuration.environments?.[0];

  const [customersContactCategories] = useState(
    configuration.customers_contacts?.categories,
  );

  const handleSuccess = useCallback(() => {
    setIsSuccess(true);
  }, []);

  const handleResetForm = useCallback(() => {
    setIsSuccess(false);
  }, []);

  return (
    <PageLayout
      title={intl.formatMessage({ defaultMessage: "Nous contacter" })}
      subtitle={intl.formatMessage({
        defaultMessage: "Rencontrez un de nos experts pour vous accompagner",
      })}>
      <Stack spacing="24px">
        <Box px=".625rem">
          <CenteredContent>
            <Stack spacing="1.5rem">
              <Flex
                flexDir={{ base: "column-reverse", lg: "row" }}
                width="full"
                gap="2.625rem">
                <Stack spacing="1rem" width={{ base: "full", lg: "70%" }}>
                  <Text variant="h3">
                    <FormattedMessage defaultMessage="Demander un rendez-vous" />
                  </Text>

                  <Box>
                    {isSuccess ? (
                      <Box pb="32px">
                        <Splash
                          title={intl.formatMessage({
                            defaultMessage:
                              "Nous avons bien reçu votre message",
                          })}
                          description={intl.formatMessage({
                            defaultMessage:
                              "Nous vous remercions et reviendrons vers vous au plus vite",
                          })}
                          src={generateFrontPath({
                            id: "@front.internal-assets",
                            parameters: {
                              filePath: "illustrations/contact-success.svg",
                            },
                            includeBasePath: true,
                          })}
                          addon={
                            <Wrap justify="center" spacing="16px">
                              <Button
                                onClick={handleResetForm}
                                variant="outline"
                                colorScheme="brandPrimary"
                                borderColor="brandPrimary.700">
                                <Text variant="button" color="brandPrimary.700">
                                  <FormattedMessage defaultMessage="Retourner au formulaire" />
                                </Text>
                              </Button>

                              <Href
                                href={generateFrontPath({
                                  id: "@front.home",
                                })}>
                                <Button
                                  colorScheme="brandPrimary"
                                  backgroundColor="brandPrimary.700">
                                  <Text variant="button" color="inherit">
                                    <FormattedMessage defaultMessage="Aller à la page d'accueil" />
                                  </Text>
                                </Button>
                              </Href>
                            </Wrap>
                          }
                        />
                      </Box>
                    ) : (
                      <>
                        {customersContactCategories && (
                          <GoogleReCaptchaProvider
                            reCaptchaKey={
                              process.env.NEXT_PUBLIC_RECAPTCHA_V3_PUBLIC_KEY ??
                              ""
                            }>
                            <ApiErrorProvider>
                              <ContactsCreateHandler
                                customersContactCategories={
                                  customersContactCategories
                                }
                                onSuccess={handleSuccess}>
                                {({ form, isLoading }) => (
                                  <ContactsCreateForm
                                    control={form.control}
                                    isLoading={isLoading}
                                    customersContactCategories={
                                      customersContactCategories
                                    }
                                  />
                                )}
                              </ContactsCreateHandler>
                            </ApiErrorProvider>
                          </GoogleReCaptchaProvider>
                        )}
                      </>
                    )}
                  </Box>
                </Stack>

                <Box width={{ base: "full", lg: "30%" }}>
                  <PersonnalInformation environment={environment} />
                </Box>
              </Flex>
            </Stack>
          </CenteredContent>
        </Box>
      </Stack>
    </PageLayout>
  );
}
